import { AxiosError } from 'axios';
import { getBaseURL, getClient } from '../BLL/clients/HttpClient';
import { ApiEndPoint } from '../BLL/Enums/ApiEndPoint';

// Define the member interface
export interface Member {
  id: number;
  firstName: string;
  lastName: string;
  relationship: string;
  dob: string;
  gender?: string;
  memberUniqueId: string;
}

// Define the steerage interface
export interface Steerage {
  id: number;
  providerName: string;
  providerAddress?: string;
  providerPhone?: string;
  providerType?: string;
  specialClaimDetails?: string;
  status: string;
  createdAt: string;
}

// Define the steerage with policy interface
export interface SteerageWithPolicy {
  id: string;
  providerName: string;
  providerAddress?: string;
  providerPhone?: string;
  providerType?: string;
  specialClaimDetails?: string;
  status: string;
  createdAt: string;
  policyNumber: string;
  authorizationCode: string;
}

// Define the VOB data interface
export interface VOBData {
  policyNumber?: string;
  memberId?: number;
  steerageId: number;
  providerName?: string;
  providerEmail?: string;
  faxNumber?: string;
  comments?: string;
}

// Define the VOB response interface
export interface VOBResponse {
  success: boolean;
  message: string;
}

/**
 * Send a VOB (Verification of Benefits) to a provider
 * @param data The VOB data to send
 * @returns A promise that resolves to the response from the server
 */
export const sendVOB = async (data: VOBData): Promise<VOBResponse> => {
  try {
    const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
    const client = getClient({ baseURL: authBaseUrl });

    // Send the VOB request to the backend
    const response = await client.post('v1/support/vob', data);

    return response.data;
  } catch (error) {
    const e = error as AxiosError;
    const errorResponse = e.response?.data as
      | { errors: { message: string }[] }
      | undefined;
    if (
      errorResponse &&
      errorResponse.errors &&
      errorResponse.errors.length > 0
    ) {
      return {
        success: false,
        message: errorResponse.errors.map((x) => x.message).join(', '),
      };
    }
    return {
      success: false,
      message:
        'An unknown error occurred while sending the VOB please contact admin',
    };
  }
};

/**
 * Fetch members by policy number
 * @param policyNumber The policy number to search for
 * @returns A promise that resolves to an array of members
 */
export const getMembersByPolicy = async (
  policyNumber: string
): Promise<Member[]> => {
  try {
    const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
    const client = getClient({ baseURL: authBaseUrl });

    // Fetch members from the backend
    const response = await client.get(
      `v1/support/members-by-policy?policyNumber=${encodeURIComponent(
        policyNumber
      )}`
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  } catch (error) {
    console.error('Error fetching members by policy:', error);
    return [];
  }
};

/**
 * Fetch steerages by member ID
 * @param memberId The member ID to search for
 * @returns A promise that resolves to an array of steerages
 */
export const getSteeragesByMember = async (
  memberId: number
): Promise<Steerage[]> => {
  try {
    const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
    const client = getClient({ baseURL: authBaseUrl });

    // Fetch steerages from the backend
    const response = await client.get(
      `v1/support/steerages-by-member?memberId=${encodeURIComponent(
        memberId.toString()
      )}`
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  } catch (error) {
    console.error('Error fetching steerages by member:', error);
    return [];
  }
};

/**
 * Fetch steerage by ID with policy number and authorization code
 * @param steerageId The steerage ID to search for
 * @returns A promise that resolves to steerage data with policy and auth code
 */
export const getSteerageById = async (
  steerageId: string
): Promise<SteerageWithPolicy | null> => {
  try {
    const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
    const client = getClient({ baseURL: authBaseUrl });

    // Fetch steerage from the backend
    const response = await client.get(
      `v1/support/steerage-by-id?steerageId=${encodeURIComponent(steerageId)}`
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return null;
  } catch (error) {
    console.error('Error fetching steerage by ID:', error);
    return null;
  }
};
